import React, { useEffect, useState } from "react";
import { CalendarClock, FileText, Loader2, Moon, Printer } from "lucide-react";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { aed, money } from "@/lib/format";
import { cn } from "@/lib/utils";

function SummaryRow({ label, value, tone = "text-slate-900", compact = false }) {
  return (
    <div className={cn("flex items-center justify-between gap-2", compact ? "text-[12px]" : "text-[13px]")}>
      <span className="text-slate-500">{label}</span>
      <span className={cn("font-mono font-semibold tabular-nums", tone)}>{value}</span>
    </div>
  );
}

function Panel({ title, icon: Icon, children, className = "" }) {
  return (
    <div className={cn("rounded-lg border border-slate-200 bg-slate-50/80 p-3", className)}>
      {title ? (
        <div className="mb-2 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
          {Icon ? <Icon className="h-3 w-3" /> : null}
          {title}
        </div>
      ) : null}
      {children}
    </div>
  );
}

/**
 * Styled confirmation before day end (replaces window.confirm).
 * Optionally print Z-report after close — user chooses format.
 */
export function DayEndConfirmDialog({
  open,
  onOpenChange,
  onConfirm,
  loading = false,
  dayToClose,
  nextOpenDate,
  counterName,
  systemDate,
  dateMismatch = false,
  countedTotal,
  currency,
  settlement = null,
  confirmLabel = "Close day",
  showPrintOption = true,
}) {
  const fmt = currency ? (n) => money(n, currency) : aed;
  const [wantPrint, setWantPrint] = useState(true);
  const [printFormat, setPrintFormat] = useState("thermal");

  useEffect(() => {
    if (!open) {
      setWantPrint(true);
      setPrintFormat("thermal");
    }
  }, [open]);

  const handleConfirm = () => {
    onConfirm?.({
      print: wantPrint,
      format: printFormat,
    });
  };

  const hasSettlement = Boolean(settlement);
  const hasCounted = countedTotal != null;

  return (
    <AlertDialog open={open} onOpenChange={(v) => !loading && onOpenChange(v)}>
      <AlertDialogContent className="flex max-h-[min(90vh,680px)] w-[calc(100vw-1.5rem)] max-w-2xl flex-col gap-0 overflow-hidden p-0 sm:rounded-2xl">
        <div className="shrink-0 bg-gradient-to-r from-slate-900 via-slate-800 to-indigo-950 px-4 py-3 text-white sm:px-5">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/10 ring-1 ring-white/20">
              <Moon className="h-4 w-4" />
            </div>
            <AlertDialogHeader className="min-w-0 space-y-0.5 text-left">
              <AlertDialogTitle className="text-base font-bold text-white">Close business day?</AlertDialogTitle>
              <AlertDialogDescription className="text-left text-[12px] leading-snug text-indigo-100/90">
                Finalises today&apos;s sales and locks POS until Day Open.
              </AlertDialogDescription>
            </AlertDialogHeader>
          </div>
        </div>

        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-3 sm:px-5 sm:py-4">
          <Panel title="Business day" icon={CalendarClock}>
            <p className="text-[13px] font-semibold leading-snug text-slate-900">
              Closing <span className="font-mono">{dayToClose || "—"}</span>
              {counterName ? (
                <>
                  {" · "}
                  <span>{counterName}</span>
                </>
              ) : null}
            </p>
            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-slate-600">
              {nextOpenDate ? (
                <span>
                  Next open: <span className="font-mono font-medium">{nextOpenDate}</span>
                </span>
              ) : null}
              {dateMismatch && systemDate && dayToClose ? (
                <span className="text-amber-700">
                  System {systemDate} — closes {dayToClose}
                </span>
              ) : null}
            </div>
          </Panel>

          {(hasSettlement || hasCounted) && (
            <div
              className={cn(
                "grid gap-3",
                hasSettlement && hasCounted ? "sm:grid-cols-2" : "grid-cols-1",
              )}
            >
              {hasSettlement ? (
                <Panel title="Settlement">
                  <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
                    <SummaryRow compact label="Cash" value={fmt(settlement.cash)} tone="text-emerald-700" />
                    <SummaryRow compact label="Card" value={fmt(settlement.card)} tone="text-indigo-700" />
                    <SummaryRow compact label="Credit" value={fmt(settlement.credit)} tone="text-amber-700" />
                    <SummaryRow compact label="Cash out" value={fmt(settlement.cashOut)} tone="text-rose-700" />
                    <div className="col-span-2 border-t border-slate-200 pt-1.5">
                      <SummaryRow compact label="Expected cash" value={fmt(settlement.expectedCash)} />
                    </div>
                  </div>
                </Panel>
              ) : null}

              {hasCounted ? (
                <Panel title="Cash count" className="border-indigo-200 bg-indigo-50/60">
                  <SummaryRow compact label="Counted cash" value={fmt(countedTotal)} tone="text-indigo-950" />
                  {settlement?.variance != null ? (
                    <div className="mt-1.5 border-t border-indigo-200/80 pt-1.5">
                      <SummaryRow
                        compact
                        label="Variance"
                        value={fmt(settlement.variance)}
                        tone={Number(settlement.variance) === 0 ? "text-slate-700" : "text-amber-700"}
                      />
                    </div>
                  ) : null}
                </Panel>
              ) : null}
            </div>
          )}

          <div className="rounded-lg border border-slate-200 p-3">
            {showPrintOption ? (
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                <div className="flex min-w-0 items-start gap-2.5">
                  <Checkbox
                    id="day-end-print"
                    checked={wantPrint}
                    onCheckedChange={(v) => setWantPrint(Boolean(v))}
                    disabled={loading}
                    className="mt-0.5"
                  />
                  <div className="min-w-0">
                    <Label htmlFor="day-end-print" className="cursor-pointer text-[12px] font-medium leading-tight">
                      Print Z-report after closing
                    </Label>
                    <p className="text-[11px] leading-snug text-slate-500">Optional — day closes either way.</p>
                  </div>
                </div>

                {wantPrint ? (
                  <div className="flex shrink-0 gap-1.5 sm:justify-end">
                    <Button
                      type="button"
                      size="sm"
                      variant={printFormat === "thermal" ? "default" : "outline"}
                      className="h-8 gap-1.5 px-3"
                      disabled={loading}
                      onClick={() => setPrintFormat("thermal")}
                    >
                      <Printer className="h-3.5 w-3.5" />
                      <span className="text-[11px] font-semibold">Thermal</span>
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant={printFormat === "a4" ? "default" : "outline"}
                      className="h-8 gap-1.5 px-3"
                      disabled={loading}
                      onClick={() => setPrintFormat("a4")}
                    >
                      <FileText className="h-3.5 w-3.5" />
                      <span className="text-[11px] font-semibold">A4</span>
                    </Button>
                  </div>
                ) : null}
              </div>
            ) : (
              <p className="text-[11px] leading-snug text-slate-500">
                Denomination will be saved and the business day closed.
              </p>
            )}
          </div>
        </div>

        <AlertDialogFooter className="shrink-0 border-t border-slate-100 bg-slate-50 px-4 py-3 sm:px-5">
          <AlertDialogCancel disabled={loading} className="h-9">
            Keep day open
          </AlertDialogCancel>
          <Button
            type="button"
            disabled={loading}
            className="h-9 bg-slate-900 hover:bg-slate-800"
            onClick={handleConfirm}
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Closing day…
              </>
            ) : (
              confirmLabel
            )}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
